/**
 * Where the AI feedback toast sits.
 *
 * Two facts drove this:
 * - Touch devices DO fire compatibility mouse events after a tap, so "has a mousemove
 *   happened" is not a touch detector: the first tap on SAVE set the pointer flag and
 *   the toast opened at the tap point, running off the right edge of a 390px screen.
 *   Touch-primary devices are detected with the pointer/hover media queries instead.
 * - With a real mouse the toast follows the cursor; near the right or bottom edge it
 *   used to leave the viewport, so it is clamped to stay fully visible.
 */

export interface ToastViewport {
  width: number;
  height: number;
}

export interface ToastPointer {
  x: number;
  y: number;
}

export interface ToastPositionInput {
  /** true on touch-primary devices ((hover: none) and (pointer: coarse)) */
  coarse: boolean;
  /** last known mouse position, or null when the mouse has not moved yet */
  pointer: ToastPointer | null;
  viewport: ToastViewport;
  bubbleWidth?: number;
  bubbleHeight?: number;
  margin?: number;
}

export const TOAST_BUBBLE_WIDTH = 300;
export const TOAST_BUBBLE_HEIGHT = 90; // two-line message + progress bar, measured 85px
export const TOAST_POINTER_OFFSET = 30;
export const TOAST_EDGE_MARGIN = 16;

/**
 * Top-centre dock used on touch devices and before the mouse has moved. Top, not bottom:
 * the toast fires right after a tap on SAVE / COMPLETE, and a bottom dock covered that
 * very bar (and the "Saved h:mm" line) for four seconds. 64px clears the activity header.
 */
export const DOCKED_TOAST_STYLE: Record<string, string> = Object.freeze({
  position: 'fixed',
  left: `${TOAST_EDGE_MARGIN}px`,
  right: `${TOAST_EDGE_MARGIN}px`,
  top: '64px',
  bottom: 'auto',
  width: 'fit-content',
  margin: '0 auto',
  maxWidth: `calc(100vw - ${TOAST_EDGE_MARGIN * 2}px)`
});

export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
}

export function toastBubbleStyle(input: ToastPositionInput): Record<string, string> {
  const {
    coarse,
    pointer,
    viewport,
    bubbleWidth = TOAST_BUBBLE_WIDTH,
    bubbleHeight = TOAST_BUBBLE_HEIGHT,
    margin = TOAST_EDGE_MARGIN
  } = input;

  if (coarse || !pointer) return { ...DOCKED_TOAST_STYLE };

  const maxX = Math.max(margin, viewport.width - bubbleWidth - margin);
  const maxY = Math.max(margin, viewport.height - bubbleHeight - margin);
  const x = Math.min(Math.max(pointer.x + TOAST_POINTER_OFFSET, margin), maxX);
  const y = Math.min(Math.max(pointer.y + TOAST_POINTER_OFFSET, margin), maxY);

  return { left: `${x}px`, top: `${y}px` };
}
